import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  attachLessonSource,
  detachLessonSource,
  lessonResearch,
  setLessonReview,
  updateLessonNotes,
  updateLessonSourceNote,
} from '@platform/client';
import { defined } from '@platform/schema';
import {
  lessonResearchSchema,
  LINE_WORD_LIMIT,
  NOTES_WORD_LIMIT,
} from '@platform/schema/authoring';
import { wordsIn } from '@platform/schema/coach-voice';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { shownDate } from '../library/feedback';
import { REVIEW_SHOWN } from '../library/status';

import { useDraft } from './draft';
import { createSourceFrom } from './source';

import type {
  LessonResearch as Research,
  SetReview,
} from '@platform/schema/authoring';

const researchKey = (lessonId: string) => ['lesson-research', lessonId];

function useResearch(lessonId: string) {
  return useQuery({
    queryKey: researchKey(lessonId),
    // Parsed rather than cast: the SDK types a nullable field as `unknown`.
    queryFn: async () =>
      lessonResearchSchema.parse(
        (await lessonResearch({ path: { id: lessonId }, throwOnError: true }))
          .data,
      ),
  });
}

/** Every write answers with the whole state, so it replaces the cache rather than refetching. */
function useResearchWrite<T>(
  lessonId: string,
  name: string,
  write: (variables: T) => Promise<{ data: unknown }>,
) {
  const queries = useQueryClient();

  return useTrackedMutation(name, {
    mutationFn: async (variables: T) =>
      lessonResearchSchema.parse((await write(variables)).data),
    onSuccess: (research) => {
      queries.setQueryData(researchKey(lessonId), research);
      void queries.invalidateQueries({ queryKey: ['lessons'] });
    },
  });
}

/** Why a lesson needs changes, at the top of its page — nothing when it does not. */
export function ReviewBanner({ lessonId }: { lessonId: string }) {
  const review = useResearch(lessonId).data?.review;
  if (review?.status !== 'NEEDS_CHANGES') return null;

  return (
    <Alert severity="error" data-testid="review-banner">
      <strong>Needs changes:</strong> {review.reason}
    </Alert>
  );
}

/** A bold `Interpretation:`, `Hypothetical:` or `Decision:` at the start of a bullet. */
const TAG = /^(Interpretation|Hypothetical|Decision):\s*/i;

function NoteBullets({ notes }: { notes: string }) {
  const lines = notes
    .split('\n')
    .map((line) => line.replace(/^\s*[-*•]\s*/, '').trim())
    .filter(Boolean);

  if (lines.length === 0)
    return (
      <Typography variant="body2" color="text.secondary">
        No notes yet.
      </Typography>
    );

  return (
    <Box component="ul" sx={{ m: 0, pl: 3 }} data-testid="research-notes">
      {lines.map((line, index) => {
        const tag = TAG.exec(line);
        return (
          <Typography component="li" variant="body2" key={index}>
            {tag === null ? (
              line
            ) : (
              <>
                <strong>{`${tag[1] ?? ''}:`}</strong>{' '}
                {line.slice(tag[0].length)}
              </>
            )}
          </Typography>
        );
      })}
    </Box>
  );
}

function WordCount({ text, limit }: { text: string; limit: number }) {
  const words = wordsIn(text);
  return (
    <Typography
      variant="caption"
      color={words > limit ? 'error' : 'text.secondary'}
    >
      {`${String(words)}/${String(limit)}`}
    </Typography>
  );
}

/**
 * What the lesson is based on — its sources, the notes on them, and whether an admin
 * has checked one against the other. Admins only; nothing here reaches a learner.
 */
export function ResearchPanel({
  lessonId,
  lessonTitle,
}: {
  lessonId: string;
  lessonTitle: string;
}) {
  const research = useResearch(lessonId);
  const data = research.data;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="research-panel">
      <Stack spacing={2}>
        <Typography variant="subtitle2">Sources and notes</Typography>
        {research.isError && (
          <Alert severity="error">Could not load the sources and notes.</Alert>
        )}
        {data !== undefined && (
          <>
            <Review lessonId={lessonId} review={data.review} />
            <Sources
              lessonId={lessonId}
              lessonTitle={lessonTitle}
              sources={data.sources}
            />
            <Notes lessonId={lessonId} notes={data.notes} />
          </>
        )}
      </Stack>
    </Paper>
  );
}

function Review({
  lessonId,
  review,
}: {
  lessonId: string;
  review: Research['review'];
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const set = useResearchWrite(lessonId, 'Set the review', (body: SetReview) =>
    setLessonReview({
      path: { id: lessonId },
      body: defined(body),
      throwOnError: true,
    }),
  );

  const shown = review === null ? undefined : REVIEW_SHOWN[review.status];
  const reasonTooLong = wordsIn(reason) > LINE_WORD_LIMIT;

  return (
    <Stack spacing={1} data-testid="review">
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', flexWrap: 'wrap' }}
      >
        <Typography variant="body2">Review:</Typography>
        {shown === undefined ? (
          <Typography variant="body2" color="text.secondary">
            No status
          </Typography>
        ) : (
          <Chip size="small" color={shown.colour} label={shown.label} />
        )}
        {review?.reviewer !== undefined && (
          <Typography variant="caption" color="text.secondary">
            {`Last reviewed by ${review.reviewer}${
              review.reviewedAt === undefined
                ? ''
                : ` on ${shownDate(review.reviewedAt)}`
            }`}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          disabled={set.isPending || review?.status === 'APPROVED'}
          onClick={() => {
            set.mutate({ status: 'APPROVED' });
          }}
        >
          Approve
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={set.isPending}
          onClick={() => {
            setAsking(true);
          }}
        >
          Needs changes
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={set.isPending}
          onClick={() => {
            set.mutate({ status: 'NEEDS_REVIEW' });
          }}
        >
          Major fact change: needs review
        </Button>
        {review !== null && (
          <Button
            size="small"
            disabled={set.isPending}
            onClick={() => {
              set.mutate({ status: null });
            }}
          >
            Clear status
          </Button>
        )}
      </Stack>

      {asking && (
        <Stack spacing={0.5}>
          <TextField
            label="What needs changing"
            size="small"
            fullWidth
            value={reason}
            error={reasonTooLong}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <WordCount text={reason} limit={LINE_WORD_LIMIT} />
            <Button
              size="small"
              variant="contained"
              disabled={set.isPending || reason.trim() === '' || reasonTooLong}
              onClick={() => {
                set.mutate(
                  { status: 'NEEDS_CHANGES', reason: reason.trim() },
                  {
                    onSuccess: () => {
                      setAsking(false);
                      setReason('');
                    },
                  },
                );
              }}
            >
              Save reason
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAsking(false);
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
      {set.isError && (
        <Alert severity="error">{describeApiError(set.error)}</Alert>
      )}
    </Stack>
  );
}

function Sources({
  lessonId,
  lessonTitle,
  sources,
}: {
  lessonId: string;
  lessonTitle: string;
  sources: Research['sources'];
}) {
  const [input, setInput] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');

  const attach = useResearchWrite(
    lessonId,
    'Add a source',
    async (request: { input: string; title: string; note: string }) => {
      const sourceId = await createSourceFrom(
        request.input,
        request.title || lessonTitle,
      );
      if (sourceId === undefined)
        throw new Error('Paste a link, or a citation of a few words.');

      return attachLessonSource({
        path: { id: lessonId },
        body: {
          sourceId,
          ...(request.note === '' ? {} : { note: request.note }),
        },
        throwOnError: true,
      });
    },
  );

  const detach = useResearchWrite(
    lessonId,
    'Remove a source',
    (sourceId: string) =>
      detachLessonSource({
        path: { id: lessonId, sourceId },
        throwOnError: true,
      }),
  );

  const renote = useResearchWrite(
    lessonId,
    'Change a source note',
    (request: { sourceId: string; note: string | null }) =>
      updateLessonSourceNote({
        path: { id: lessonId, sourceId: request.sourceId },
        body: { note: request.note },
        throwOnError: true,
      }),
  );

  const noteTooLong = wordsIn(note) > LINE_WORD_LIMIT;

  return (
    <Stack spacing={1}>
      <Typography variant="body2">Sources</Typography>
      {sources.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No sources yet.
        </Typography>
      )}
      <Stack component="ol" spacing={1} sx={{ m: 0, pl: 3 }}>
        {sources.map((source) => (
          <Box component="li" key={source.id} data-testid="research-source">
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {source.url === undefined ? (
                  (source.citation ?? source.title)
                ) : (
                  <Link href={source.url} target="_blank" rel="noreferrer">
                    {source.title}
                  </Link>
                )}
              </Typography>
              <IconButton
                size="small"
                aria-label={`Remove ${source.title}`}
                disabled={detach.isPending}
                onClick={() => {
                  detach.mutate(source.id);
                }}
              >
                ×
              </IconButton>
            </Stack>
            <SourceNote
              value={source.note ?? ''}
              onSave={(next) => {
                renote.mutate({ sourceId: source.id, note: next || null });
              }}
            />
          </Box>
        ))}
      </Stack>

      <Stack spacing={1} sx={{ pt: 1 }}>
        <TextField
          label="Add a source: a link, or a citation"
          size="small"
          fullWidth
          value={input}
          onChange={(event) => {
            setInput(event.target.value);
          }}
        />
        <Stack direction="row" spacing={1}>
          <TextField
            label="Title (optional)"
            size="small"
            sx={{ flex: 1 }}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          />
          <TextField
            label="What it backs here (optional)"
            size="small"
            sx={{ flex: 2 }}
            value={note}
            error={noteTooLong}
            helperText={<WordCount text={note} limit={LINE_WORD_LIMIT} />}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </Stack>
        <Box>
          <Button
            size="small"
            variant="outlined"
            disabled={attach.isPending || input.trim() === '' || noteTooLong}
            onClick={() => {
              attach.mutate(
                {
                  input: input.trim(),
                  title: title.trim(),
                  note: note.trim(),
                },
                {
                  onSuccess: () => {
                    setInput('');
                    setTitle('');
                    setNote('');
                  },
                },
              );
            }}
          >
            Add source
          </Button>
        </Box>
      </Stack>
      {[attach, detach, renote].map((write, index) =>
        write.isError ? (
          <Alert severity="error" key={index}>
            {describeApiError(write.error)}
          </Alert>
        ) : null,
      )}
    </Stack>
  );
}

/** A source's one-line note, saved on blur. Emptying it removes it. */
function SourceNote({
  value,
  onSave,
}: {
  value: string;
  onSave: (next: string) => void;
}) {
  const { draft, setDraft, reset } = useDraft(value);
  const tooLong = wordsIn(draft) > LINE_WORD_LIMIT;

  return (
    <TextField
      variant="standard"
      size="small"
      fullWidth
      placeholder="What it backs in this lesson"
      value={draft}
      error={tooLong}
      helperText={tooLong && <WordCount text={draft} limit={LINE_WORD_LIMIT} />}
      slotProps={{ htmlInput: { 'aria-label': 'Source note' } }}
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onBlur={() => {
        const next = draft.trim();
        if (next === value) {
          reset();
          return;
        }
        if (!tooLong) onSave(next);
      }}
    />
  );
}

function Notes({ lessonId, notes }: { lessonId: string; notes: string }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(notes);
  const save = useResearchWrite(lessonId, 'Save the notes', (text: string) =>
    updateLessonNotes({
      path: { id: lessonId },
      body: { notes: text },
      throwOnError: true,
    }),
  );

  const over = wordsIn(draft) > NOTES_WORD_LIMIT;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          Notes
        </Typography>
        {!editing && (
          <Button
            size="small"
            onClick={() => {
              setDraft(notes);
              save.reset();
              setEditing(true);
            }}
          >
            Edit notes
          </Button>
        )}
      </Stack>

      {!editing && <NoteBullets notes={notes} />}

      {editing && (
        <>
          <TextField
            label="Notes: one bullet per line"
            helperText="Start a line with Interpretation:, Hypothetical: or Decision: to tag it."
            multiline
            minRows={4}
            fullWidth
            size="small"
            value={draft}
            error={over}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <WordCount text={draft} limit={NOTES_WORD_LIMIT} />
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              onClick={() => {
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={save.isPending}
              onClick={() => {
                save.mutate(draft, {
                  onSuccess: () => {
                    setEditing(false);
                  },
                });
              }}
            >
              Save notes
            </Button>
          </Stack>
          {save.isError && (
            <Alert severity="error" data-testid="notes-error">
              {describeApiError(save.error)}
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
}
